import { useEffect, useState } from "react";
import { Badge, Button, Col, Row, Table } from "reactstrap";
import FilterBadge from "../../common/FilterBadge";
import FilterInput from "../../common/FilterInput";
import { isMultiValue, KaSelect, KaSelectOption, toKaSelectOptions } from "../../common/KaSelect";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import MultiQueryView from "../../common/lib/MultiQueryView";
import useGetSiteSettings from "../../requests/useGetSiteSettings";
import { StagedPurchaseOrderDetails, StagedPurchaseOrderStep, useGetStagedPurchaseOrders } from "../../requests/useGetStagedPurchaseOrders";
import { BaySettings } from "./BayDirections/BayDirections";
import StagedPurchaseOrderFilterModal from "./StagedPurchaseOrderFilterModal";
import useLocalStorage from "../../common/lib/hooks/useLocalStorage";

export type FilterSettings = {
  bay: string;
  stagedPurchaseOrderStep: string;
}

export enum SelectableColumns {
    Product = "Product",
    Amount = "Amount",
    Status = "Status",
    Bay = "Bay",
    Supplier = "Supplier",
    Created = "Created",
}

const StagedPurchaseOrders = () => {
  const displayMessage = useDisplayMessage();
  const useGetStagedPurchaseOrdersQuery = useGetStagedPurchaseOrders();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);

  const [selectedColumns, setSelectedColumns] = useLocalStorage<string[]>("stagedPurchaseOrders-selectedColumns", Object.values(SelectableColumns).map(s => s.toString()));

  const getBaySettings = useGetSiteSettings<BaySettings>();

  const [filters, setFilters] = useLocalStorage<FilterSettings>("stagedPurchaseOrders-filters", {
    bay: '',
    stagedPurchaseOrderStep: '',
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) =>
    setFilters({ ...filters, ...newFilters });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    return () => clearTimeout(handler);
  }, [search]);

  const stagedPurchaseOrderStepOptions = toKaSelectOptions(Object.values(StagedPurchaseOrderStep).map(s => ({ label: s.replace(/([A-Z])/g, ' $1').trim(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );
  const filterStep = stagedPurchaseOrderStepOptions.find((option) => option.value === filters.stagedPurchaseOrderStep);

  const bayOptions = toKaSelectOptions(getBaySettings.data?.bays ?? [],
    "id",
    "name",
    { sortByLabel: true }
  );
  const filterBay = bayOptions.find((option) => option.value === filters.bay);

  const stagedOrdersFiltered = useGetStagedPurchaseOrdersQuery.data?.filter((spo) =>
    (spo.purchaseOrderNumber.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      spo.product.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      spo.supplier.toLowerCase().includes(debouncedSearch.toLowerCase())) &&
    (filters.bay ? spo.bay === filterBay?.label : true) &&
    (filters.stagedPurchaseOrderStep ? spo.step === filterStep?.value : true)
  );

  const selectableColumnOptions = toKaSelectOptions(Object.values(SelectableColumns).map<KaSelectOption>(s => ({ label: s.toString(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );

  return <MultiQueryView
    queries={{ stagedPurchaseOrders: useGetStagedPurchaseOrdersQuery, bays: getBaySettings }}
    renderData={response => <>
      <div className="d-flex justify-content-between">
        <h2>Staged Purchase Orders</h2>
        <div>
          <FilterBadge
            label="Status"
            value={filterStep?.label}
            onClose={() => updateFilters({ stagedPurchaseOrderStep: '' })} />
          <FilterBadge
            label="Bay"
            value={filterBay?.label}
            onClose={() => updateFilters({ bay: '' })} />
          <Button className="py-0"
            color="link"
            data-testid="refresh-button"
            onClick={() => useGetStagedPurchaseOrdersQuery.query()}>
            <i className="bi-arrow-clockwise" style={{ fontSize: '2rem' }} />
          </Button>
        </div>
      </div>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <Row>
        <Col className="d-flex justify-content-between my-2">
          <form className="col col-lg-5 my-auto mx-3">
            <Row>
              <Col className="col-auto px-0 m-auto flex-fill">
                <FilterInput placeholder='Search products, suppliers...' value={search} onFilterTextChange={setSearch} />
              </Col>
              <Col className="col-auto px-2">
                <Button className="p-0"
                  color="link"
                  data-testid="show-filter-modal"
                  onClick={() => setShowFilterModal(true)}>
                  <i className="bi-filter" style={{ fontSize: '2rem' }} />
                </Button>
              </Col>
            </Row> 
          </form>
          <Col className="col-auto my-auto">
            <Row>
              <Col className="col-auto m-auto">
                <KaSelect
                  placeholder="Select columns"
                  options={selectableColumnOptions}
                  isClearable={true}
                  controlShouldRenderValue={false}
                  closeMenuOnSelect={false}
                  isMulti={true}
                  hideSelectedOptions={false}
                  onChange={(valueObject) => {
                    if (isMultiValue(valueObject)) { setSelectedColumns(valueObject?.map(p => p.value)) }
                  }}
                  value={selectableColumnOptions.filter((option) => selectedColumns.includes(option.value))} />
              </Col>
            </Row>
          </Col>
        </Col>
      </Row>
      <Table className="p-3 mb-3">
        <thead>
          <tr className="p-1 mb-3 bg-primary bg-opacity-10">
            <th>Purchase Order #</th>
            {selectedColumns.includes("Product") && <th>Product</th>}
            {selectedColumns.includes("Amount") && <th>Amount</th>}
            {selectedColumns.includes("Status") && <th>Status</th>}
            {selectedColumns.includes("Bay") && <th>Bay</th>}
            {selectedColumns.includes("Supplier") && <th>Supplier</th>}
            {selectedColumns.includes("Created") && <th>Created</th>}
          </tr>
        </thead>
        <tbody>
          {stagedOrdersFiltered!.sort(alphabeticallyByName).map((spo) =>
            <tr key={spo.id} className="border-bottom border-1">
              <td>{spo.purchaseOrderNumber}</td>
              {selectedColumns.includes("Product") && <td>{spo.product}</td>}
              {selectedColumns.includes("Amount") && <td>{spo.requested + " lb"}</td>}
              {selectedColumns.includes("Status") && <td>{formatStagedPurchaseOrderStep(spo.step)}</td>}
              {selectedColumns.includes("Bay") && <td>{spo.bay}</td>}
              {selectedColumns.includes("Supplier") && <td>{spo.supplier}</td>}
              {selectedColumns.includes("Created") && <td>{new Date(spo.createdAt).toLocaleDateString()}</td>}
            </tr>
          )}
        </tbody>
      </Table>
      {showFilterModal && <StagedPurchaseOrderFilterModal
        bayOptions={bayOptions}
        stagedPurchaseOrderStepOptions={stagedPurchaseOrderStepOptions}
        filters={filters}
        updateFilters={updateFilters}
        toggle={() => setShowFilterModal(false)} />}
    </>
    } />
}

const formatStagedPurchaseOrderStep = (step: StagedPurchaseOrderStep) => <Badge className={`bg-stagedOrderStep-${step.split('Receiving')[1]}`} style={{ textWrap: "wrap" }}>{step.split('Receiving')[1].replace(/([A-Z])/g, ' $1').trim()}</Badge>;

const alphabeticallyByName = (a: StagedPurchaseOrderDetails, b: StagedPurchaseOrderDetails) => a.purchaseOrderNumber.localeCompare(b.purchaseOrderNumber);

export { StagedPurchaseOrders };